import {
  BACKLOG_CAP, BACKLOG_FORGIVEN, BACKLOG_RANGE, DAY_LENGTH, DEMAND_GROWTH,
  DEMAND_SUPPLY_RATIO, DRAIN_MARGIN, OUTAGE_FACTOR, PRODUCER_BUILD_COST,
  PRODUCER_UPGRADE_COST,
  PRODUCER_UPGRADE_COST_GROWTH, PRODUCER_UPGRADE_STEP,
  GLUT_FACTOR, GOOD_IDS_ALL, GOODS, LOAD_TIME, MILESTONES, RECIPE,
  REFINERY_RATE, ROUTE_COST_PER_PX,
  TRUCK_CAPACITY,
  TRUCK_COST,
  TRUCK_REFUND,
  TRUCK_SPEED, TRUCK_UPKEEP, TRUST_MIN, TRUST_RATE, UNLOAD_TIME,
  CLOSURE_FACTOR, VIA_DETOUR_MAX, MAX_REGIONS, REGION_BASE_COST,
} from "./constants";
import {
  demandSwapOn, disruptionsOn, retirementOn, throughputOn, upkeepFactor,
} from "./disruption";
import { congestionFactor, distance, drainable, laneCost, laneUpkeep, routeThroughput } from "./haul";
import type { Milestone } from "./constants";
import type {
  BuildResult, CityNode, Corridor, Disruption, GameNode, GameState, GoodId, MilestoneId,
  ProducedGoodId, ProducerNode, Route, SimEvent, ThroughputDisruption, TransferNode,
  Truck, ExpandRegionResult,
} from "./types";
import { allotDemand, commissionProducer, demandRatio, extensionFor, generateRegion } from "./world";

// The truck arithmetic lives in haul.ts so that world.ts can reach it without
// importing this module, which imports world.ts. Re-exported because every
// caller outside core/ has always found it here.
export * from "./haul";

// ─── Hilfsfunktionen ────────────────────────────────────────────────────────

export const clamp = (v: number, lo: number, hi: number): number =>
  v < lo ? lo : v > hi ? hi : v;

export function findNode(s: GameState, id: number): GameNode {
  const n = s.nodes.find((x) => x.id === id);
  if (!n) throw new Error(`Knoten ${id} existiert nicht`);
  return n;
}

export function findRoute(s: GameState, id: number): Route | undefined {
  return s.routes.find((r) => r.id === id);
}

/** The end a truck loads at. Throws if the world is malformed; save.ts prevents that. */
export function sourceOf(s: GameState, r: Route): ProducerNode | TransferNode {
  const n = findNode(s, r.from);
  if (n.kind === "city") throw new Error(`Strecke ${r.id} startet an einer Stadt`);
  return n;
}

/** The end a truck unloads at. Same guarantee. */
export function sinkOf(s: GameState, r: Route): CityNode | TransferNode {
  const n = findNode(s, r.to);
  if (n.kind === "producer") throw new Error(`Strecke ${r.id} endet an einem Produzenten`);
  return n;
}

/** Units of `good` available at a source. A producer's whole stock is its own good. */
export const stockOf = (n: ProducerNode | TransferNode, good: GoodId): number =>
  n.kind === "producer" ? (n.good === good ? n.stock : 0) : n.stock[good];

/**
 * Every entry of a per-good record, summed. One helper for the four places that
 * flatten one: a hub's stock against its shared cap, and a city's demand and
 * backlog wherever the presentation still shows one number (RFC-0016 §3.6).
 */
export const sumRecord = (r: Record<GoodId, number>): number =>
  Object.values(r).reduce((sum, v) => sum + v, 0);

/** Everything in a hub, all goods together. Compared against `cap`. */
export const totalStock = (n: TransferNode): number => sumRecord(n.stock);

/** What a city is owed across every good — what trust is computed from. */
export const totalBacklog = (n: CityNode): number => sumRecord(n.backlog);

/** What a city wants per second across every good. */
export const totalDemand = (n: CityNode): number => sumRecord(n.demand);

export const routeCost = (a: GameNode, b: GameNode): number =>
  Math.round(distance(a, b) * ROUTE_COST_PER_PX);

export const trucksOnRoute = (s: GameState, routeId: number) =>
  s.trucks.filter((t) => t.route === routeId);

/** Has this world reached that milestone? The gate RFC-0003..0005 read. */
export const hasMilestone = (s: GameState, id: MilestoneId): boolean =>
  s.milestones.includes(id);

/** The next milestone, or null once all are reached. Drives the HUD bar. */
export const nextMilestone = (s: GameState): Milestone | null =>
  MILESTONES.find((m) => !s.milestones.includes(m.id)) ?? null;

export const regionExpansionCost = (s: GameState): number => REGION_BASE_COST * s.regionCount;

export function expandRegion(s: GameState): ExpandRegionResult {
  if (!hasMilestone(s, "veredelung")) return { ok: false, reason: "locked" };
  if (s.regionCount >= MAX_REGIONS) return { ok: false, reason: "limit-reached" };
  const cost = regionExpansionCost(s);
  if (s.money < cost) return { ok: false, reason: "insufficient-funds" };
  const nodes = generateRegion(s);
  if (!nodes) return { ok: false, reason: "placement-failed" };
  s.money -= cost;
  s.nodes.push(...nodes);
  s.regionCount++;
  return { ok: true, region: s.regionCount, nodes: nodes.map((n) => n.id) };
}

// ─── Aktionen ───────────────────────────────────────────────────────────────

/**
 * Baut eine Strecke.
 *
 * A city is never a source and a producer is never a sink, so a pair naming them
 * the wrong way round is flipped; every other pair keeps its argument order,
 * because transfer → transfer has no direction to infer.
 *
 * `good` is ignored when the source is a producer and required when it is a
 * transfer node — a hub holds two goods and the route has to say which one it
 * hauls.
 */
/**
 * The corridor with this id, or a throw. Same contract as findNode(): a
 * dangling reference is a broken world, not a case to recover from.
 */
export function corridorOf(s: GameState, id: number): Corridor {
  const c = s.corridors.find((x) => x.id === id);
  if (!c) throw new Error(`Kein Korridor mit id ${id}`);
  return c;
}

/** The corridors a route runs over, in travel order. */
export const corridorsOfRoute = (s: GameState, r: Route): Corridor[] =>
  r.corridors.map((id) => corridorOf(s, id));

/**
 * The nodes a route passes, in travel order — two for a direct road, three for
 * one with a stop.
 *
 * The renderer needs it to draw a path instead of a straight line, and the hit
 * test needs it for the same reason: a road drawn through a waypoint that can
 * only be clicked on the chord is a road that lies about where it is.
 */
export function routePoints(s: GameState, r: Route): GameNode[] {
  const points = [findNode(s, r.from)];
  for (const id of r.corridors) {
    const c = corridorOf(s, id);
    const here = points[points.length - 1]!;
    points.push(findNode(s, c.from === here.id ? c.to : c.from));
  }
  return points;
}

/**
 * The fractions of `pos` at which a waypoint sits, in order, without the two
 * ends (RFC-0039 §3.2).
 *
 * Derived rather than stored, and that is what keeps this RFC free of a
 * migration: a truck standing at a stop has `pos` exactly on that fraction, so
 * "the first fraction strictly greater than pos" is the next one and the truck
 * needs no memory of where it has been.
 */
export function stopPoints(s: GameState, r: Route): number[] {
  if (r.corridors.length < 2) return [];
  const total = routeLength(s, r);
  if (total <= 0) return [];
  const out: number[] = [];
  let run = 0;
  for (const id of r.corridors.slice(0, -1)) {
    run += corridorOf(s, id).length;
    out.push(run / total);
  }
  return out;
}

/** How far a truck on this route travels one way — the sum of its sections. */
export const routeLength = (s: GameState, r: Route): number =>
  r.corridors.reduce((sum, id) => sum + corridorOf(s, id).length, 0);

/**
 * The lanes that matter for this route: the narrowest section it runs over,
 * because that is the one that jams first. With one corridor it is that
 * corridor's, which is what it was before RFC-0033.
 */
export const routeLanes = (s: GameState, r: Route): number =>
  Math.min(...corridorsOfRoute(s, r).map((c) => c.lanes));

/**
 * Trucks per corridor — the counter RFC-0033 exists for.
 *
 * Before this, `step()` counted per route, so two roads over the same stretch
 * were two independent streets no matter how exactly they overlapped. A truck
 * occupies **every** section it runs over, because it is on all of them during
 * one round trip and the jam it causes does not care which of them it is on
 * right now.
 */
export function corridorLoad(s: GameState): Map<number, number> {
  const perRoute = new Map<number, number>();
  for (const t of s.trucks) perRoute.set(t.route, (perRoute.get(t.route) ?? 0) + 1);
  const load = new Map<number, number>();
  for (const r of s.routes) {
    const n = perRoute.get(r.id) ?? 0;
    for (const id of r.corridors) load.set(id, (load.get(id) ?? 0) + n);
  }
  return load;
}

/**
 * How fast a truck on this route moves: the **slowest** section it runs over.
 *
 * Not the mean, and that is invariant I6 rather than a preference. A mean nets
 * one jammed kilometre against the free ones until the bottleneck is invisible,
 * and the bottleneck is the thing this document was written to create.
 *
 * A corridor with no entry in `load` carries no trucks — which cannot happen
 * for a route that has one, but the renderer asks about routes it is drawing
 * before the frame has moved anything.
 */
export function routeFactor(
  s: GameState, r: Route, load: Map<number, number>, unlocked: boolean,
): number {
  let worst = 1;
  for (const id of r.corridors) {
    const c = s.corridors.find((x) => x.id === id);
    if (!c) continue;
    worst = Math.min(worst, congestionFactor(c.length, load.get(id) ?? 0, unlocked, c.lanes));
  }
  return worst;
}

/**
 * The section of this route the player would widen: the slowest one under the
 * traffic it carries right now, ties going to the shortest.
 *
 * "Slowest" rather than "narrowest by capacity" because that is the question the
 * player is actually asking — a long empty section has less capacity per truck
 * and is still not the problem. Shortest as the tie-break, because on an empty
 * route every section runs at 1 and the short one is the one that jams first.
 */
export function narrowest(s: GameState, r: Route, load?: Map<number, number>): Corridor {
  const traffic = load ?? corridorLoad(s);
  const sections = r.corridors.map((id) => corridorOf(s, id));
  const worst = sections.reduce((best, c) => {
    const f = (x: Corridor) => congestionFactor(x.length, traffic.get(x.id) ?? 0, true, x.lanes);
    if (f(c) < f(best)) return c;
    return f(c) === f(best) && c.length < best.length ? c : best;
  }, sections[0]!);
  return worst;
}

/** The corridor between these two nodes, either way round, or none. */
export const corridorBetween = (s: GameState, a: number, b: number): Corridor | undefined =>
  s.corridors.find((c) => (c.from === a && c.to === b) || (c.from === b && c.to === a));

/** Builds a corridor between two nodes. */
function makeCorridor(s: GameState, a: GameNode, b: GameNode): Corridor {
  const corridor: Corridor = {
    id: s.nextCorridorId++,
    from: a.id,
    to: b.id,
    length: distance(a, b),
    lanes: 1,
  };
  s.corridors.push(corridor);
  return corridor;
}

export function buildRoute(
  s: GameState, aId: number, bId: number, good?: GoodId,
): BuildResult {
  let a = findNode(s, aId);
  let b = findNode(s, bId);
  if (a.kind === "city" || b.kind === "producer") [a, b] = [b, a];
  if (a.kind === "city" || b.kind === "producer") return { ok: false, reason: "invalid-pair" };
  if (a.id === b.id) return { ok: false, reason: "invalid-pair" };

  // A works that has closed is not a source (RFC-0017 §3.5). Its leftover stock
  // is worth a few loads, and selling the player a road for them would be
  // selling a road to a node that can never fill it again.
  if (a.kind === "producer" && a.rate <= 0) return { ok: false, reason: "invalid-pair" };

  const cargo = a.kind === "producer" ? a.good : good;
  if (!cargo) return { ok: false, reason: "missing-good" };
  // Same pair twice is a duplicate only if it also hauls the same good: two
  // goods out of one hub are two roads, not one road twice.
  if (s.routes.some((r) => r.from === a.id && r.to === b.id && r.good === cargo)) {
    return { ok: false, reason: "duplicate" };
  }

  const cost = routeCost(a, b);
  if (s.money < cost) return { ok: false, reason: "insufficient-funds" };

  s.money -= cost;
  // Its own corridor, always: sharing one is RFC-0033 PR 3, and until then a
  // route that got somebody else's would change what the player already owns.
  const corridor = makeCorridor(s, a, b);
  const route: Route = {
    id: s.nextRouteId++,
    from: a.id,
    to: b.id,
    good: cargo,
    corridors: [corridor.id],
  };
  s.routes.push(route);
  return { ok: true, routeId: route.id };
}

/** A cheaper way to connect two nodes, by running over roads that already exist. */
export interface ViaOption {
  /** The node the path stops at. */
  via: number;
  /** What building it costs — only the legs nobody has built yet. */
  cost: number;
  /** How much longer the path is than the straight road, as a factor. */
  detour: number;
  /** How many routes are already on the legs this one would share. */
  shares: number;
}

/**
 * The waypoints worth offering for this connection, cheapest first.
 *
 * Only ones that are **cheaper than the straight road**, which by the triangle
 * inequality means at least one leg already exists (RFC-0033 §1.4: a two-leg
 * path is never shorter, 0 of 1320). So this is empty on an empty map and fills
 * up as the network does, which is the pacing the mechanic wants.
 *
 * It deliberately does not rank by anything but price. What the detour costs in
 * congestion is the decision, and RFC-0033 §4 refuses to make it for the player
 * — `detour` and `shares` are here so the panel can *show* the trade.
 */
export function viaOptions(s: GameState, aId: number, bId: number): ViaOption[] {
  const a = findNode(s, aId);
  const b = findNode(s, bId);
  const straight = distance(a, b);
  const direct = routeCost(a, b);
  const out: ViaOption[] = [];
  for (const m of s.nodes) {
    if (m.id === aId || m.id === bId) continue;
    const legs: [GameNode, GameNode][] = [[a, m], [m, b]];
    const existing = legs.map(([x, y]) => corridorBetween(s, x.id, y.id));
    if (!existing.some(Boolean)) continue;
    const cost = legs.reduce((sum, [x, y], i) => sum + (existing[i] ? 0 : routeCost(x, y)), 0);
    if (cost >= direct) continue;
    const detour = (distance(a, m) + distance(m, b)) / straight;
    // Past the measured cliff it is not a decision, it is a trap (§1.17).
    if (detour > VIA_DETOUR_MAX) continue;
    const shares = existing.reduce(
      (n, c) => n + (c ? s.routes.filter((r) => r.corridors.includes(c.id)).length : 0),
      0,
    );
    out.push({ via: m.id, cost, detour, shares });
  }
  return out.sort((x, y) => x.cost - y.cost || x.detour - y.detour);
}

/**
 * A route that runs through an intermediate node, reusing the corridors that
 * are already there (RFC-0033 PR 3).
 *
 * The trade this exists for: a connection is 47 % road (§1.9), and a two-leg
 * path over an existing leg pays only for the missing one — at the price of
 * sharing that leg's capacity with whoever else is on it. Measured, it is worth
 * it while the detour stays near 2 % and loses money past 10 % (§1.10).
 *
 * **Any** node may be the waypoint, works included, and that was measured
 * rather than assumed. The first version refused a works, reasoning that a
 * truck cannot unload there — but nothing unloads at a waypoint in this design:
 * the truck drives A → via → B and does nothing on the way. The restriction was
 * argued from a schedule this document explicitly does not have (§2), and it
 * cost half the options and 2,4 points of the measured gain (§1.17).
 *
 * No schedule and no partial unloading. That is #58's "smallest form of a line",
 * and the rest of it is a document of its own.
 */
export function buildRouteVia(
  s: GameState, aId: number, bId: number, viaId: number, good?: GoodId,
): BuildResult {
  if (viaId === aId || viaId === bId || aId === bId) return { ok: false, reason: "invalid-pair" };
  let a = findNode(s, aId);
  let b = findNode(s, bId);
  const via = findNode(s, viaId);   // throws if it is not a node at all
  if (a.kind === "city" || b.kind === "producer") [a, b] = [b, a];
  if (a.kind === "city" || b.kind === "producer") return { ok: false, reason: "invalid-pair" };
  if (a.kind === "producer" && a.rate <= 0) return { ok: false, reason: "invalid-pair" };

  const cargo = a.kind === "producer" ? a.good : good;
  if (!cargo) return { ok: false, reason: "missing-good" };
  if (s.routes.some((r) => r.from === a.id && r.to === b.id && r.good === cargo)) {
    return { ok: false, reason: "duplicate" };
  }

  // A corridor already on the board is free to use and is what makes this
  // cheaper than the straight road; a missing one is built and paid for.
  const legs: [GameNode, GameNode][] = [[a, via], [via, b]];
  const existing = legs.map(([x, y]) => corridorBetween(s, x.id, y.id));
  const cost = legs.reduce(
    (sum, [x, y], i) => sum + (existing[i] ? 0 : routeCost(x, y)),
    0,
  );
  if (s.money < cost) return { ok: false, reason: "insufficient-funds" };

  s.money -= cost;
  const corridors = legs.map(([x, y], i) => (existing[i] ?? makeCorridor(s, x, y)).id);
  const route: Route = {
    id: s.nextRouteId++,
    from: a.id,
    to: b.id,
    good: cargo,
    corridors,
  };
  s.routes.push(route);
  return { ok: true, routeId: route.id };
}

export function buyTruck(s: GameState, routeId: number): boolean {
  if (s.money < TRUCK_COST) return false;
  if (!findRoute(s, routeId)) return false;
  s.money -= TRUCK_COST;
  s.trucks.push({
    id: s.nextTruckId++,
    route: routeId,
    pos: 0,
    cargo: 0,
    state: "loading",
    timer: 0,
    idle: 0,
  });
  return true;
}

/**
 * Widens a route by one lane (RFC-0014). No upper bound: the source's rate is
 * the ceiling, so lane n + 1 on a saturated road buys nothing and costs upkeep
 * every day — the same self-punishment overcapacity already has.
 *
 * The milestone gate sits here rather than only in the panel. Without `verkehr`
 * congestionFactor() returns 1 whatever the traffic, so a lane would be a
 * purchase with no effect, and a rule the UI alone enforces is a rule that only
 * holds while somebody is looking at the UI.
 */
export function buyLane(s: GameState, corridorId: number): boolean {
  if (!hasMilestone(s, "verkehr")) return false;
  const corridor = s.corridors.find((c) => c.id === corridorId);
  if (!corridor) return false;
  const cost = laneCost(corridor.length);
  if (s.money < cost) return false;
  s.money -= cost;
  corridor.lanes++;
  return true;
}

/** What the next upgrade of this producer costs. Rises with its level. */
export const producerUpgradeCost = (level: number): number =>
  Math.round(PRODUCER_UPGRADE_COST * PRODUCER_UPGRADE_COST_GROWTH ** level);

/**
 * Buys a producer more output — and, in the same step, the mouths for it.
 *
 * That coupling is the whole of RFC-0017 §3.2. Raising `rate` alone would lower
 * the demand-to-supply ratio and sell the player a smaller value for their own
 * cargo; raising demand afterwards would leave a moment where the invariant is
 * broken and a rule has to run to fix it. One step, and
 * `Σ demandMax[g] = DEMAND_SUPPLY_RATIO × Σ rate[g]` is preserved exactly.
 *
 * Refused when the step would push the producer past what one road to a city
 * wanting its good can carry: the generator has refused to *create* an
 * undrainable producer since RFC-0005, and an upgrade must not manufacture one.
 * Refused too when nobody wants the good at all — supply with no mouth is the
 * inversion §1.3 exists to prevent.
 */
export function upgradeProducer(s: GameState, nodeId: number): boolean {
  if (!canUpgrade(s, nodeId)) return false;
  const p = findNode(s, nodeId) as ProducerNode;

  const cost = producerUpgradeCost(p.level);
  if (s.money < cost) return false;

  // The ratio this good currently runs at, read before the rate moves. See
  // demandRatio(): on a map with a refinery it is below the constant, and
  // paying the constant would drift the map with every purchase.
  const ratio = demandRatio(s, p.good);
  s.money -= cost;
  p.rate += PRODUCER_UPGRADE_STEP;
  p.level++;
  // Appetite where the goods can go (RFC-0036). Split by what this works
  // actually moves to each city, not by what each city already wants: a plain
  // pro-rata share raises the ceiling of cities this works has no road to, and
  // measured that cost 0,046 mean trust and broke the guard's long-run bound.
  //
  // Throughput rather than `drainable(L) × lanes`, and that is measured too: a
  // road nobody drives on counts fully in the capacity version, which left one
  // of three seeds under the bound. Weight 0 everywhere — a works with no road
  // yet — falls back to today's split inside allotDemand().
  const reach = (c: CityNode): number => s.routes
    .filter((r) => r.from === p.id && r.to === c.id)
    .reduce((sum, r) => sum + routeThroughput(
      routeLength(s, r), trucksOnRoute(s, r.id).length, routeLanes(s, r)), 0);
  allotDemand(s, p.good, PRODUCER_UPGRADE_STEP * ratio, reach);
  return true;
}

/**
 * Whether the *road* allows another step — money is a separate question.
 *
 * Exported so the panel can tell the two refusals apart without a second copy of
 * the rule: "not enough money yet" is a wait, "the road is the bottleneck" is a
 * different purchase entirely, and a card that says only "no" teaches neither.
 *
 * The whole step has to fit rather than being clamped: a purchase that buys a
 * sliver is one the player cannot judge.
 */
export function canUpgrade(s: GameState, nodeId: number): boolean {
  const p = s.nodes.find((n) => n.id === nodeId);
  if (!p || p.kind !== "producer") return false;
  // A ruin is not restarted for the price of a step. Reviving a closed works
  // for PRODUCER_UPGRADE_COST would be the cheap way to what PRODUCER_BUILD_COST
  // buys, and it would turn a setback into a toll — the answer to a closure is
  // the works somewhere else (RFC-0017 §3.5).
  if (p.rate <= 0) return false;
  const takers = s.nodes.filter((n): n is CityNode =>
    n.kind === "city" && n.demandMax[p.good] > 0);
  if (takers.length === 0) return false;
  const best = Math.min(...takers.map((c) => distance(p, c)));
  // RFC-0035 §3.2: the lanes of the road the player actually built to a taker,
  // 1 when there is none. The narrowest section binds. drainable() stays
  // single-lane — RFC-0005 §3 promises a plain road empties this works, and a
  // promise is a floor, not a ceiling.
  const road = s.routes.find((r) => r.from === p.id && takers.some((c) => c.id === r.to));
  const lanes = road
    ? Math.min(...road.corridors.map((id) => corridorOf(s, id).lanes))
    : 1;
  return drainable(best) * lanes * DRAIN_MARGIN - p.rate >= PRODUCER_UPGRADE_STEP;
}

/**
 * Commissions a new producer of `good` — and, in the same step, the mouths for
 * it (RFC-0017 §3.4).
 *
 * The second of the two levers, and it obeys the same rule as the first: money
 * buys supply *and* the demand that goes with it, never supply alone. What
 * arrives is whatever the generator decided the spot can carry, so the demand
 * allotted is measured off the node rather than off the base rate.
 *
 * Refused when nobody wants the good and when the board has no room — see
 * commissionProducer(), which owns both answers.
 */
export function buildProducer(s: GameState, good: ProducedGoodId): boolean {
  if (s.money < PRODUCER_BUILD_COST) return false;
  const p = commissionProducer(s, good);
  if (!p) return false;

  // Before the node joins the board, or the new works would be counted as part
  // of the supply that establishes the ratio it is being measured against.
  const ratio = demandRatio(s, good);
  s.money -= PRODUCER_BUILD_COST;
  s.nodes.push(p);
  allotDemand(s, good, p.rate * ratio);
  return true;
}

// ─── The world takes one back (RFC-0017 §3.5) ───────────────────────────────

/**
 * A producer whose whole output one road to this city could carry, or null.
 *
 * The form the reachability goal of §2 has in code: a city is served when some
 * works makes something it wants *and* sits close enough that a single road
 * empties that works into it. `drainable()` falls with length, so this is a
 * statement about the geometry rather than about what the player has built.
 *
 * The generator satisfies it for all but 4 of 5 500 cities measured, because a
 * producer's rate is capped against its nearest taker. What could break it is a
 * closure, and that is exactly why this function exists — see mayRetire().
 */
export function supplierFor(s: GameState, c: CityNode): ProducerNode | null {
  return s.nodes.find((n): n is ProducerNode =>
    n.kind === "producer" && n.rate > 0 && c.demandMax[n.good] > 0
    && drainable(distance(n, c)) >= n.rate) ?? null;
}

/**
 * Whether this works may close. Three bounds, all derived rather than typed.
 *
 * 1. **Never the last of its good.** Demand is withdrawn in proportion, so the
 *    last source would take every mouth for that good with it, and a city whose
 *    only appetite it was would be left with none — invariant 3.
 * 2. **Never past RFC-0012's headroom.** The survivors' shares grow when one of
 *    them goes, and a single outage still has to fit under `1 −
 *    DEMAND_SUPPLY_RATIO`. That bounds any one producer at
 *    `(1 − DEMAND_SUPPLY_RATIO) / (1 − OUTAGE_FACTOR)` of total production.
 * 3. **Never strands a city.** §2 promises every city can be served by some
 *    road that pays full price, and this document may not be the thing that
 *    breaks it. A bound, not a repair: the alternative — placing a rescue works
 *    afterwards — was measured to be needed on 6 of 2 400 closures, which is a
 *    mechanic nobody would ever meet and a second way for supply to appear
 *    without being paid for (RFC-0017 §3.5).
 */
function mayRetire(s: GameState, p: ProducerNode): boolean {
  const rest = s.nodes.filter((n): n is ProducerNode =>
    n.kind === "producer" && n.rate > 0 && n !== p);
  if (!rest.some((q) => q.good === p.good)) return false;

  const supply = rest.reduce((sum, q) => sum + q.rate, 0);
  const cap = (1 - DEMAND_SUPPLY_RATIO) / (1 - OUTAGE_FACTOR);
  if (supply <= 0 || rest.some((q) => q.rate / supply > cap)) return false;

  // The withdrawal has to fit in the ceilings it comes out of. What leaves is
  // measured against the whole claim on this good — the cities' own appetite
  // *and* what the recipe eats for their werkzeug (demandRatio()) — but only the
  // cities' appetite can be taken away. Comfortable on every shipped map; a
  // check rather than an argument, because the alternative is a silent
  // under-withdrawal and an unsolvable good.
  const raw = s.nodes.reduce((sum, n) =>
    sum + (n.kind === "city" ? n.demandMax[p.good] : 0), 0);
  if (p.rate * demandRatio(s, p.good) > raw) return false;

  const others = s.nodes.filter((n) => n !== p);
  return s.nodes.every((n) => n.kind !== "city"
    || supplierFor(s, n) === null
    || supplierFor({ ...s, nodes: others }, n) !== null);
}

/**
 * The producer a closure names, or null when the one it named may not go.
 *
 * Sorted by id, never by list order, for the reason pickTarget() sorts: a
 * milestone appends to `s.nodes`. And no retry when the bounds refuse — the day
 * simply passes without a closure, which keeps "at most one" construction
 * rather than a search that has to be bounded.
 */
export function pickRetirement(s: GameState, target: number): ProducerNode | null {
  const live = s.nodes
    .filter((n): n is ProducerNode => n.kind === "producer" && n.rate > 0)
    .sort((a, b) => a.id - b.id);
  const p = live[Math.floor(target * live.length)];
  return p && mayRetire(s, p) ? p : null;
}

/**
 * Closes a works: its rate goes to 0, and its mouths go with it.
 *
 * The mirror of buildProducer(), and it has to be one. `Σ demandMax[g] =
 * DEMAND_SUPPLY_RATIO × Σ rate[g]` is an equality, so supply that leaves without
 * its demand puts the map above its own production overnight — the unsolvable
 * board test/balance.test.ts exists to catch.
 *
 * The node stays. It keeps its name, its place and the roads the player paid to
 * build to it, and those roads keep their trucks standing in front of an empty
 * store with the `!` the game already draws. A node that vanished would take
 * routes the player bought with it, which is a harsher setback than anything
 * else in this game — and the sunk cost is the story here.
 */
export function retireProducer(s: GameState, p: ProducerNode): void {
  const lost = p.rate;
  const ratio = demandRatio(s, p.good);
  p.rate = 0;
  allotDemand(s, p.good, -lost * ratio);
}

/**
 * Gives back one truck and keeps the road. Returns false when this route has
 * none (RFC-0018).
 *
 * The pick is the truck with the least cargo, ties going to the highest id: the
 * load on board is lost, so the cheapest one to lose goes first, and among
 * equals the newest — the one most likely to be the purchase that was wrong.
 */
export function sellTruck(s: GameState, routeId: number): boolean {
  let pick: Truck | null = null;
  for (const t of s.trucks) {
    if (t.route !== routeId) continue;
    if (!pick || t.cargo < pick.cargo || (t.cargo === pick.cargo && t.id > pick.id)) pick = t;
  }
  if (!pick) return false;
  s.trucks = s.trucks.filter((t) => t !== pick);
  s.money += Math.round(TRUCK_REFUND * TRUCK_COST);
  return true;
}

/** Reißt die Strecke ab. Die zugehörigen Lkw sind ersatzlos weg. */
export function sellRoute(s: GameState, routeId: number): void {
  const gone = s.routes.find((r) => r.id === routeId);
  s.trucks = s.trucks.filter((t) => t.route !== routeId);
  s.routes = s.routes.filter((r) => r.id !== routeId);
  // A corridor outlives the route only while somebody else is on it. Leaving
  // orphans behind would keep charging lane upkeep for a road with no traffic —
  // and from PR 3 on it would silently hand the next route a free shortcut.
  if (!gone) return;
  const used = new Set(s.routes.flatMap((r) => r.corridors));
  s.corridors = s.corridors.filter((c) => used.has(c.id) || !gone.corridors.includes(c.id));
}

// ─── Störungsziel ───────────────────────────────────────────────────────────

/** Which node or route a throughput disruption landed on. Both null means none. */
export interface Target {
  nodeId: number | null;
  routeId: number | null;
}

const NO_TARGET: Target = { nodeId: null, routeId: null };

/**
 * Turns the disruption's `target` fraction into something on this map.
 *
 * Sorted by id, never by list order: `s.nodes` is in generation order and
 * `s.routes` is in build order, and the player changes the second one by selling.
 *
 * Two hops are accepted rather than fixed (RFC-0012 §3.5). A milestone landing
 * mid-disruption grows the producer list, so the same fraction can move to
 * another producer — the bound in §3.6 holds for any single one, so this is a
 * story rather than a bug. Selling a route mid-closure moves the closure, and
 * the alternative is the schedule in the save file that RFC-0011 refused.
 *
 * An empty list resolves to no target: a world without routes has no throughput
 * to disrupt, and that needs no special case anywhere else.
 */
/**
 * The two cities a swap names, or null when this world has fewer than two.
 *
 * Sorted by id for the same reason pickTarget() sorts: `s.nodes` is in
 * generation order, and a milestone appends to it.
 *
 * The second fraction indexes the list *without* the first city, then steps over
 * it — that is what makes the pair distinct by construction rather than by a
 * retry loop, which would need a bound and would still be able to fail.
 */
export function pickSwap(
  s: GameState, pair: readonly [number, number],
): [CityNode, CityNode] | null {
  const cities = s.nodes
    .filter((n): n is CityNode => n.kind === "city")
    .sort((x, y) => x.id - y.id);
  if (cities.length < 2) return null;
  const a = Math.min(cities.length - 1, Math.floor(pair[0] * cities.length));
  let b = Math.min(cities.length - 2, Math.floor(pair[1] * (cities.length - 1)));
  if (b >= a) b++;
  return [cities[a]!, cities[b]!];
}

/**
 * The route a closure is throttling right now, or null.
 *
 * One question, one answer, three askers: `step()` drives on it, the panel prints
 * it and the renderer draws the barrier from it. Each of them deriving it from
 * `throughputOn()` and `pickTarget()` on its own is how the closure came to be
 * invisible in the first place (RFC-0012 §9.2) — and how a sold route would come
 * to move the barrier without moving the slowdown.
 *
 * Cheap enough to call once a frame and no cheaper: `pickTarget()` sorts the
 * routes. Callers that draw every route ask once and compare ids, rather than
 * asking per route.
 */
export function closedRoute(s: GameState): number | null {
  const hit = throughputOn(s.seed, s.day);
  return hit?.kind === "closure" ? pickTarget(s, hit).routeId : null;
}

/**
 * A route named the way the map shows it: „Sägewerk Nord → Ahlbeck".
 *
 * The chip used to say „Strecke 3", and a route id is drawn nowhere — so the one
 * disruption a player can actually answer was the one they could not locate.
 */
export const routeLabel = (s: GameState, routeId: number): string => {
  const r = findRoute(s, routeId);
  if (!r) return "";
  return `${findNode(s, r.from).name} → ${findNode(s, r.to).name}`;
};

export function pickTarget(s: GameState, d: ThroughputDisruption): Target {
  if (d.kind === "closure") {
    const routes = [...s.routes].sort((a, b) => a.id - b.id);
    return { nodeId: null, routeId: routes[Math.floor(d.target * routes.length)]?.id ?? null };
  }

  const wanted = d.kind === "outage" ? "producer" : "city";
  const nodes = s.nodes.filter((n) => n.kind === wanted).sort((a, b) => a.id - b.id);
  return { nodeId: nodes[Math.floor(d.target * nodes.length)]?.id ?? null, routeId: null };
}

// ─── Simulationsschritt ─────────────────────────────────────────────────────

/**
 * Advances the world by `dt` seconds and reports what happened. Pure: no DOM,
 * no randomness, no clock. That is what makes it testable.
 *
 * There is no terminal state. A world runs on through a negative balance and
 * past the last milestone — the only way a step does nothing is `dt = 0`.
 */
export function step(s: GameState, dt: number): SimEvent[] {
  const events: SimEvent[] = [];

  // The weather of the day that is currently running (RFC-0011). Derived, never
  // stored — and read once here rather than per truck, because the upkeep charged
  // at the boundary below belongs to *this* day, not to the one after it.
  const active = disruptionsOn(s.seed, s.day);

  // The throughput weather (RFC-0012), resolved once per step rather than once
  // per node: pickTarget() sorts a list, and doing that inside the loop below
  // would be the same answer computed ten times.
  const hit = throughputOn(s.seed, s.day);
  const where = hit ? pickTarget(s, hit) : NO_TARGET;

  // A plain transfer node has nothing to tick: it produces nothing and demands
  // nothing, and only trucks move its stock. A refinery is the exception since
  // RFC-0015 — it is the one node that changes its own stock without a truck.
  for (const n of s.nodes) {
    if (n.kind === "producer") {
      // An outage is the producer producing less — no new term, just its own
      // rate through a factor.
      const rate = hit?.kind === "outage" && where.nodeId === n.id
        ? n.rate * hit.factor
        : n.rate;
      n.stock = Math.min(n.cap, n.stock + rate * dt);
    } else if (n.kind === "city") {
      // A slump is the city wanting less — of everything it wants. It moves
      // solvability the safe way and punishes the fleet that was right yesterday
      // through RFC-0008: what nobody is waiting for pays GLUT_FACTOR.
      const slumped = hit?.kind === "slump" && where.nodeId === n.id ? hit.factor : 1;
      // Each good runs its own backlog against its own cap. That is what makes a
      // two-good city reach TRUST_MIN about twice as fast, and the reason
      // TWO_GOOD_CITIES is a measured knob rather than taste (RFC-0016 §3.6).
      for (const g of GOOD_IDS_ALL) {
        n.backlog[g] = Math.min(BACKLOG_CAP, n.backlog[g] + n.demand[g] * slumped * dt);
      }
      const owed = totalBacklog(n);
      const target = clamp(1 - (owed - BACKLOG_FORGIVEN) / BACKLOG_RANGE, TRUST_MIN, 1);
      n.trust += (target - n.trust) * Math.min(1, TRUST_RATE * dt);
    } else if (n.kind === "transfer" && n.refines) {
      // RFC-0015 §3.2. Whichever runs out first decides — the rate, the holz or
      // the erz — so a lopsided feed converts nothing and the surplus input just
      // sits there, occupying a store both goods share.
      //
      // No room check and no blocked state: two units go in and one comes out,
      // so totalStock() always falls and a refinery can never jam itself on its
      // own output (§3.8, invariant 4).
      //
      // No event either. A `refined` event would fire thirty times a second, and
      // the fill ring is the feedback for everything else a hub does.
      const made = Math.min(
        REFINERY_RATE * dt,
        n.stock.holz / RECIPE.holz,
        n.stock.erz / RECIPE.erz,
      );
      if (made > 0) {
        n.stock.holz -= made * RECIPE.holz;
        n.stock.erz -= made * RECIPE.erz;
        n.stock.werkzeug += made;
      }
    }
  }

  // One pass, not trucksOnRoute() per truck — that would be O(trucks²) per frame.
  // The whole fleet counts, including trucks standing at a dock: a driving-only
  // count would flicker between frames and would not match the number the panel
  // shows the player.
  const load = corridorLoad(s);
  const jamsUnlocked = hasMilestone(s, "verkehr");
  // Everything the truck loop looks up, indexed once per frame. Length and lanes
  // are a lookup through s.corridors, and the loop wants them three times per
  // truck; the route and its two ends were a linear findRoute()/findNode() each,
  // which is O(trucks × (routes + nodes)) — 8 190 comparisons a frame on today's
  // map, 74 151 on one with twice the nodes. Summed per truck rather than cached
  // per route on purpose: a cached geometry is a second source of truth for
  // length, which is the thing RFC-0033 §3.3 moved to the corridor to avoid.
  //
  // Plain arrays, and that is measured rather than taste. Three seeds × 200 days,
  // twelve interleaved runs each, median: 6 166 ms as it stood, 5 822 ms with
  // Maps filled by a for-loop, 4 287 ms with these — the cost was `Map.get()`
  // itself, not the pair arrays a `new Map(xs.map(...))` allocates. The obvious
  // shape is the slow one, so do not tidy this into a Map.
  //
  // ponytail: indexed by id, and ids are not reused — a player who sells enough
  // routes leaves holes. Fill stays far off V8's dictionary-mode threshold at
  // any plausible route count; if it ever does not, the fallback is the Map
  // variant above, which is still faster than the linear scan.
  const byCorridor: (Corridor | undefined)[] = [];
  for (const c of s.corridors) byCorridor[c.id] = c;
  const byRoute: (Route | undefined)[] = [];
  for (const r of s.routes) byRoute[r.id] = r;
  const byNode: (GameNode | undefined)[] = [];
  for (const n of s.nodes) byNode[n.id] = n;

  for (const t of s.trucks) {
    const route = byRoute[t.route];
    if (!route) continue;
    let geoLength = 0;
    let factor = 1;
    for (const id of route.corridors) {
      const c = byCorridor[id];
      if (!c) continue;
      geoLength += c.length;
      factor = Math.min(
        factor,
        congestionFactor(c.length, load.get(id) ?? 0, jamsUnlocked, c.lanes),
      );
    }
    if (geoLength <= 0) continue;
    // sourceOf()/sinkOf() through the index. Those two throw on a malformed
    // world; here a bad end skips the truck, exactly as the missing route a
    // dozen lines up does. save.ts validates both before a state ever runs.
    const source = byNode[route.from];
    const sink = byNode[route.to];
    if (!source || source.kind === "city") continue;
    if (!sink || sink.kind === "producer") continue;
    // A closure rides the congestion lever rather than adding one: the route
    // panel's speed readout and jamLevel() then show it without knowing it
    // exists, and the player reads it with the vocabulary they already have.
    const closed = hit?.kind === "closure" && where.routeId === route.id;
    const speed = TRUCK_SPEED
      * factor * (closed ? CLOSURE_FACTOR : 1);

    switch (t.state) {
      case "loading": {
        // A stop on the way is `loading` with pos > 0 — the one place where a
        // loading truck is not standing at its own start (RFC-0039 §3.3).
        if (t.pos > 0) {
          const idx = stopPoints(s, route).indexOf(t.pos);
          const stop = byNode[routePoints(s, route)[idx + 1]?.id ?? -1];
          if (!stop || stop.kind !== "producer") { t.state = "outbound"; break; }
          t.timer += dt;
          if (t.timer >= LOAD_TIME) {
            const take = Math.min(TRUCK_CAPACITY - t.cargo, Math.floor(stop.stock));
            stop.stock -= take;
            t.cargo += take;
            t.timer = 0;
            t.state = "outbound";
          }
          break;
        }
        const available = stockOf(source, route.good);
        if (available < 1) {
          // Engpass: Lager leer, der Lkw steht und kostet trotzdem. An empty hub
          // stalls its outbound truck exactly like an empty producer does.
          t.idle += dt;
          break;
        }
        t.idle = 0;
        t.timer += dt;
        if (t.timer >= LOAD_TIME) {
          const take = Math.min(TRUCK_CAPACITY, Math.floor(available));
          if (source.kind === "producer") source.stock -= take;
          else source.stock[route.good] -= take;
          t.cargo = take;
          t.timer = 0;
          t.state = "outbound";
        }
        break;
      }
      case "outbound": {
        const from = t.pos;
        t.pos += (speed * dt) / geoLength;
        // A works passed on the way is cargo passed on the way (RFC-0039).
        // Measured, a truck leaves with 2 of 8 units because it takes what is
        // there, not what it can carry — so the stop is worth its LOAD_TIME
        // whenever anything is lying there at all.
        if (t.cargo < TRUCK_CAPACITY) {
          const points = stopPoints(s, route);
          const at = points.find((p) => p > from && p <= t.pos);
          if (at !== undefined) {
            const idx = points.indexOf(at);
            const stop = byNode[routePoints(s, route)[idx + 1]?.id ?? -1];
            // No idle here: an empty stop is not a blockage, it is nothing to
            // do. The truck rolls through and the `!` stays off.
            if (stop && stop.kind === "producer" && stop.good === route.good
              && stop.stock >= 1) {
              t.pos = at;
              t.state = "loading";
              t.timer = 0;
              break;
            }
          }
        }
        if (t.pos >= 1) {
          t.pos = 1;
          t.state = "unloading";
          t.timer = 0;
        }
        break;
      }
      case "unloading": {
        // ponytail: all or nothing. The last TRUCK_CAPACITY - 1 units of a hub
        // stay unusable; partial drops would need a second cargo counter for no
        // new decision.
        // A refinery keeps room for the good it is *not* being handed. Measured
        // without this, the shared store is the whole mechanic's undoing: the
        // faster of the two feeds fills all 60 units, the slower one can then
        // never unload, and both inputs are present only 4 % of the time — a
        // deadlock the player has no lever against, because a feed cannot be
        // staffed with less than one truck (RFC-0015 §7). Half the store per
        // input, which for a 1:1 recipe with two of them is the only division
        // that treats them alike.
        const room = sink.kind === "transfer" && sink.refines && route.good !== "werkzeug"
          ? sink.cap / Object.keys(RECIPE).length
          : Infinity;
        if (
          sink.kind === "transfer"
          && (totalStock(sink) + t.cargo > sink.cap || sink.stock[route.good] + t.cargo > room)
        ) {
          t.idle += dt; // hub full — the truck waits, and keeps paying upkeep
          break;
        }
        t.idle = 0;
        t.timer += dt;
        if (t.timer >= UNLOAD_TIME) {
          if (sink.kind === "city") {
            // A load is worth what the city was waiting for — of *this* good.
            // The rest still arrives, and still counts against that good's
            // backlog, but it sells at GLUT_FACTOR, so the route that is needed
            // is the route that pays.
            //
            // Nothing here knows about wanted and unwanted goods, and nothing
            // needs to: a city that does not want erz has backlog.erz === 0
            // forever, so `wanted` is 0 and the whole load pays the surplus
            // price. "Accepted at the surplus price" is what this formula
            // already did, once the backlog knew about goods (RFC-0016 §3.3).
            const wanted = Math.min(t.cargo, sink.backlog[route.good]);
            const paid = wanted + (t.cargo - wanted) * GLUT_FACTOR;
            const amount = paid * GOODS[route.good].price * sink.trust;
            s.money += amount;
            // Revenue, not balance: reinvesting every euro must not slow the
            // player's progress down, and hoarding must not speed it up.
            s.earned += amount;
            sink.backlog[route.good] = Math.max(0, sink.backlog[route.good] - t.cargo);
            events.push({ type: "delivered", nodeId: sink.id, units: t.cargo, amount });
          } else {
            // No money, no event: nothing was sold, and the fill ring is the
            // feedback.
            sink.stock[route.good] += t.cargo;
          }
          t.cargo = 0;
          t.timer = 0;
          t.state = "returning";
        }
        break;
      }
      case "returning": {
        t.pos -= (speed * dt) / geoLength;
        if (t.pos <= 0) {
          t.pos = 0;
          t.state = "loading";
          t.timer = 0;
        }
        break;
      }
    }
  }

  s.dayTime += dt;
  if (s.dayTime >= DAY_LENGTH) {
    s.dayTime -= DAY_LENGTH;
    s.day++;
    // One bill, two sources. The fleet's half rides the weather (RFC-0011); the
    // road's does not — fuel and the haulage rebate are about running trucks,
    // and putting asphalt on that axis would re-tune a test measured without it.
    const roads = s.corridors.reduce((sum, c) => sum + laneUpkeep(c.length, c.lanes), 0);
    const upkeep = s.trucks.length * TRUCK_UPKEEP * upkeepFactor(active) + roads;
    if (upkeep > 0) {
      s.money -= upkeep;
      events.push({ type: "upkeep", amount: upkeep });
    }
    for (const n of s.nodes) {
      if (n.kind !== "city") continue;
      // Per good, against its own ceiling. A zero appetite stays zero, because
      // growth is multiplicative — that is what keeps the full record honest.
      for (const g of GOOD_IDS_ALL) {
        n.demand[g] = Math.min(n.demandMax[g], n.demand[g] * DEMAND_GROWTH);
      }
    }
    events.push({ type: "dayChanged", day: s.day });

    // What the new day brings, against what the old one had. (startDay, kind) is
    // the identity: one day rolls at most one disruption, so two of a kind can
    // never share a start.
    const after = disruptionsOn(s.seed, s.day);
    const same = (a: Disruption, b: Disruption) =>
      a.startDay === b.startDay && a.kind === b.kind;
    for (const d of after) {
      if (!active.some((b) => same(b, d))) {
        events.push({ type: "disruptionStarted", disruption: d });
      }
    }
    for (const d of active) {
      if (!after.some((a) => same(a, d))) {
        events.push({ type: "disruptionEnded", disruption: d });
      }
    }

    // The same comparison for the throughput kind, which is a single value
    // rather than a list — startDay identifies it, because at most one exists.
    const nextHit = throughputOn(s.seed, s.day);
    if (nextHit && nextHit.startDay !== hit?.startDay) {
      // Resolved against the map as it stands at this line. A milestone landing
      // in the same boundary grows it a few lines below, and then the HUD — which
      // asks pickTarget() itself — will name a different node than this event
      // carries. Once per world per milestone, on the announcement day, with the
      // overlay covering the map anyway. Not worth reordering a day boundary for.
      const landed = pickTarget(s, nextHit);
      events.push({
        type: "throughputStarted",
        disruption: nextHit,
        nodeId: landed.nodeId,
        routeId: landed.routeId,
      });
    }
    if (hit && hit.startDay !== nextHit?.startDay) {
      events.push({ type: "throughputEnded", disruption: hit });
    }

    // The appetite swap (RFC-0016 §3.5). Applied once and then *kept*, unlike
    // the weather above — which is why it sits here and not in the per-frame
    // section: asking again tomorrow would apply the same swap twice.
    //
    // Whole records change hands, so `Σ demandMax[g]` is identical afterwards
    // for every good and solvability stays construction rather than a check.
    // Backlog and trust stay behind: the holz a city was owed is still owed,
    // still pays full price and still counts against its trust, which is the
    // grace period that makes a shift a setback rather than a wall.
    const swap = demandSwapOn(s.seed, s.day);
    const pair = swap ? pickSwap(s, swap) : null;
    if (pair) {
      const [a, b] = pair;
      const demand = a.demand;
      a.demand = b.demand;
      b.demand = demand;
      const max = { ...a.demandMax };
      Object.assign(a.demandMax, b.demandMax);
      Object.assign(b.demandMax, max);
      events.push({ type: "demandShifted", a: a.id, b: b.id });
    }

    // A works closes (RFC-0017 §3.5). Applied once, like the swap above and for
    // the same reason: this is not weather that runs for a few days, it is the
    // world afterwards. Before the milestone below, so a stage that lands on the
    // same boundary cannot have one of its own producers closed on arrival.
    const closing = retirementOn(s.seed, s.day);
    const doomed = closing === null ? null : pickRetirement(s, closing);
    if (doomed) {
      retireProducer(s, doomed);
      events.push({ type: "producerClosed", nodeId: doomed.id });
    }

    // Once per day, not once per frame: it makes the trigger independent of the
    // frame rate and puts the map change on the boundary the player is already
    // being told about.
    const next = nextMilestone(s);
    if (next && s.earned >= next.earned) {
      s.milestones.push(next.id);
      // No id filter any more: since RFC-0005 the extension is generated from
      // the world's own seed and its ids continue past the highest one on the
      // board, so a collision is impossible by construction rather than by
      // check. What a hand-edited save with the nodes but not the milestone now
      // gets is a second, differently placed batch — a crowded map instead of a
      // broken findNode(), which is the better of the two.
      s.nodes.push(...extensionFor(s, next.id));
      events.push({ type: "milestone", id: next.id });
    }
  }

  return events;
}
